import React, { useId } from 'react';

export type YotpoStyleStarRatingProps = {
  rating: number;
};

/**
 * Renders star rating with Yotpo-style visual design.
 * Empty stars have black borders to match Yotpo's design.
 * Used for Mixed Case products where we calculate average rating from Commerce Tools.
 */
export const YotpoStyleStarRating: React.FC<YotpoStyleStarRatingProps> = ({ rating }) => {
  // Generate a stable base ID using React's useId hook (avoids Math.random() issues)
  const baseId = useId();

  if (!rating || rating <= 0) {
    return null;
  }

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  // Generate unique gradient ID for each star using stable base ID
  const generateGradientId = (index: number, type: 'full' | 'empty') =>
    `yotpo_stars_gradient_${baseId}_${type}_${index}`;

  // Filled star SVG - black with gradient (matching Yotpo)
  const filledStar = (index: number) => {
    const gradientId = generateGradientId(index, 'full');
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 18 18"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        style={{ display: 'flex', flexDirection: 'row', marginInlineEnd: '3.5px' }}
      >
        <defs>
          <linearGradient id={gradientId}>
            <stop offset="100%" stopColor="#000000" />
            <stop stopOpacity="1" offset="100%" stopColor="#FFFFFF" />
          </linearGradient>
        </defs>
        <path
          style={{ pointerEvents: 'none' }}
          d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z"
          stroke="#000000"
          fill={`url(#${gradientId})`}
        />
      </svg>
    );
  };

  // Half star SVG - black with gradient
  const halfStar = () => {
    const gradientId = generateGradientId(0, 'full');
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 18 18"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        style={{ display: 'flex', flexDirection: 'row', marginInlineEnd: '3.5px' }}
      >
        <defs>
          <linearGradient id={gradientId}>
            <stop offset="50%" stopColor="#000000" />
            <stop stopOpacity="1" offset="50%" stopColor="#FFFFFF" />
          </linearGradient>
        </defs>
        <path
          style={{ pointerEvents: 'none' }}
          d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z"
          stroke="#000000"
          fill={`url(#${gradientId})`}
        />
      </svg>
    );
  };

  // Empty star SVG with black border (Yotpo style)
  const emptyStar = (index: number) => {
    const gradientId = generateGradientId(index, 'empty');
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 18 18"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        style={{ display: 'flex', flexDirection: 'row', marginInlineEnd: '8px' }}
      >
        <defs>
          <linearGradient id={gradientId}>
            <stop offset="0%" stopColor="#000000" />
            <stop stopOpacity="1" offset="0%" stopColor="#FFFFFF" />
          </linearGradient>
        </defs>
        <path
          style={{ pointerEvents: 'none' }}
          d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z"
          stroke="#000000"
          fill={`url(#${gradientId})`}
        />
      </svg>
    );
  };

  return (
    <span
      className="star-icons"
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        height: '28px',
        cursor: 'pointer',
      }}
    >
      {Array.from({ length: fullStars }, (_, i) => (
        <span key={`filled-${i}`}>{filledStar(i)}</span>
      ))}
      {hasHalfStar && <span key="half">{halfStar()}</span>}
      {Array.from({ length: emptyStars }, (_, i) => (
        <span key={`empty-${i}`}>{emptyStar(i)}</span>
      ))}
    </span>
  );
};
